import { gql } from '@apollo/client';
import { TABLE_TRANSLATION_ITEM_FIELDS } from '../queries/config';

/**
 * System configuration — the writes behind the console's Configuration tabs.
 *
 * Grouped by what they cost. The removals need confirming, the bootstrap
 * loaders come first on an empty database, the data loads are long and heavy,
 * and the recalculations only mean anything once a load has finished.
 */

/* ── risk configuration ─────────────────────────────────────────────────────
   Cloning matters more than it looks: a scenario is long to describe and
   usually a variation of one that exists, so clone-then-edit is the way these
   are actually made. The last two RUN something rather than storing it. */

export const CREATE_RISK_SETTINGS = gql`
  mutation CreateRiskSettings($inputData: RiskSettingsInput!) {
    createRiskSettings(inputData: $inputData) {
      createdAt
      description
      endDate
      id
      isActive
      isGlobal
      name
      riskSensitivityScenarioIds
      riskStressScenarioIds
      startDate
      userId
    }
  }
`;

export const UPDATE_RISK_SETTINGS = gql`
  mutation UpdateRiskSettings($settingsId: Int!, $inputData: RiskSettingsUpdateInput!) {
    updateRiskSettings(settingsId: $settingsId, inputData: $inputData) {
      createdAt
      description
      endDate
      id
      isActive
      isGlobal
      name
      riskSensitivityScenarioIds
      riskStressScenarioIds
      startDate
      userId
    }
  }
`;

export const DELETE_RISK_SETTINGS = gql`
  mutation DeleteRiskSettings($settingsId: Int!) {
    deleteRiskSettings(settingsId: $settingsId)
  }
`;

export const CLONE_RISK_SETTINGS = gql`
  mutation CloneRiskSettings($settingsId: Int!, $newName: String!) {
    cloneRiskSettings(settingsId: $settingsId, newName: $newName) {
      id
      name
      description
    }
  }
`;

export const CREATE_RISK_STRESS_SCENARIO = gql`
  mutation CreateRiskStressScenario($inputData: RiskStressScenarioInput!) {
    createRiskStressScenario(inputData: $inputData) {
      id
      scenarioName
      securitySymbol
      securityType
      description
      historicalEndDate
      isActive
      isHistorical
      isStandardDeviation
      historicalStartDate
      numberOfStandardDeviations
      shockValueNegativeAmount
      shockValuePositiveAmount
      shockValueNegativePercent
      shockValuePositivePercent
    }
  }
`;

export const UPDATE_RISK_STRESS_SCENARIO = gql`
  mutation UpdateRiskStressScenario($scenarioId: Int!, $inputData: RiskStressScenarioUpdateInput!) {
    updateRiskStressScenario(scenarioId: $scenarioId, inputData: $inputData) {
      id
      scenarioName
      securitySymbol
      securityType
      description
      historicalEndDate
      isActive
      isHistorical
      isStandardDeviation
      historicalStartDate
      numberOfStandardDeviations
      shockValueNegativeAmount
      shockValuePositiveAmount
      shockValueNegativePercent
      shockValuePositivePercent
    }
  }
`;

export const DELETE_RISK_STRESS_SCENARIO = gql`
  mutation DeleteRiskStressScenario($scenarioId: Int!) {
    deleteRiskStressScenario(scenarioId: $scenarioId)
  }
`;

export const CLONE_RISK_STRESS_SCENARIO = gql`
  mutation CloneRiskStressScenario($scenarioId: Int!, $newScenarioName: String!
    $newScenarioValue: String!) {
    cloneRiskStressScenario(
      scenarioId: $scenarioId
      newScenarioName: $newScenarioName
      newScenarioValue: $newScenarioValue
    ) {
      id
      scenarioName
    }
  }
`;

export const CREATE_RISK_SENSITIVITY_SCENARIO = gql`
  mutation CreateRiskSensitivityScenario($inputData: RiskSensitivityScenarioInput!) {
    createRiskSensitivityScenario(inputData: $inputData) {
      id
      scenarioName
      securitySymbol
      securityType
      transitivityType
      isStandardDeviation
      numberOfStandardDeviations
      shockValueNegativeAmount
      shockValueNegativePercent
      shockValuePositiveAmount
      shockValuePositivePercent
    }
  }
`;

export const UPDATE_RISK_SENSITIVITY_SCENARIO = gql`
  mutation UpdateRiskSensitivityScenario($scenarioId: Int!, $inputData: RiskSensitivityScenarioUpdateInput!) {
    updateRiskSensitivityScenario(scenarioId: $scenarioId, inputData: $inputData) {
      id
      scenarioName
      securitySymbol
      securityType
      transitivityType
      isStandardDeviation
      numberOfStandardDeviations
      shockValueNegativeAmount
      shockValueNegativePercent
      shockValuePositiveAmount
      shockValuePositivePercent
    }
  }
`;

export const DELETE_RISK_SENSITIVITY_SCENARIO = gql`
  mutation DeleteRiskSensitivityScenario($scenarioId: Int!) {
    deleteRiskSensitivityScenario(scenarioId: $scenarioId)
  }
`;

export const CLONE_RISK_SENSITIVITY_SCENARIO = gql`
  mutation CloneRiskSensitivityScenario($scenarioId: Int!, $newScenarioName: String!
    $newScenarioValue: String!) {
    cloneRiskSensitivityScenario(
      scenarioId: $scenarioId
      newScenarioName: $newScenarioName
      newScenarioValue: $newScenarioValue
    ) {
      id
      scenarioName
    }
  }
`;

export const CREATE_RISK_SIMULATION_CONFIGURATION = gql`
  mutation CreateRiskSimulationConfiguration($inputData: RiskSimulationConfigurationInput!) {
    createRiskSimulationConfiguration(inputData: $inputData) {
      id
      userTag
      ownerId
      scenarioName
      halfLifeDays
      historicWindowSize
      isExponential
      currency
      numberOfSimulations
      forecastingHorizon
      isGlobal
      description
      createdAt
    }
  }
`;

export const UPDATE_RISK_SIMULATION_CONFIGURATION = gql`
  mutation UpdateRiskSimulationConfiguration($configId: Int!, $inputData: RiskSimulationConfigurationUpdateInput!) {
    updateRiskSimulationConfiguration(configId: $configId, inputData: $inputData) {
      id
      userTag
      ownerId
      scenarioName
      halfLifeDays
      historicWindowSize
      isExponential
      currency
      numberOfSimulations
      forecastingHorizon
      isGlobal
      description
      createdAt
    }
  }
`;

export const DELETE_RISK_SIMULATION_CONFIGURATION = gql`
  mutation DeleteRiskSimulationConfiguration($configId: Int!) {
    deleteRiskSimulationConfiguration(configId: $configId)
  }
`;

export const CLONE_RISK_SIMULATION_CONFIGURATION = gql`
  mutation CloneRiskSimulationConfiguration($configId: Int!, $newScenarioName: String!
    $newScenarioValue: String!) {
    cloneRiskSimulationConfiguration(configId: $configId, newScenarioName: $newScenarioName
      newScenarioValue: $newScenarioValue) {
      id
      userTag
      ownerId
      scenarioName
      halfLifeDays
      historicWindowSize
      isExponential
      currency
      numberOfSimulations
      forecastingHorizon
      isGlobal
      description
      createdAt
    }
  }
`;

export const TRIGGER_PARAMETRIC_RISK_SIMULATION = gql`
  mutation TriggerParametricRiskSimulation(
    $scenarioName: String!
    $topN: Int
    $incremental: Boolean
    $forceRecompute: Boolean
  ) {
    triggerParametricRiskSimulation(
      scenarioName: $scenarioName
      topN: $topN
      incremental: $incremental
      forceRecompute: $forceRecompute
    ) {
      success
      message
      factorCount
      symbolCount
      dateCount
    }
  }
`;

export const BUILD_UNIVERSE_MEAN_COVARIANCE = gql`
  mutation BuildUniverseMeanCovariance(
    $scenarioName: String!
    $topN: Int
    $incremental: Boolean
    $saveCorrelationArtifacts: Boolean
  ) {
    buildUniverseMeanCovariance(
      scenarioName: $scenarioName
      topN: $topN
      incremental: $incremental
      saveCorrelationArtifacts: $saveCorrelationArtifacts
    ) {
      success
      message
      factorCount
      symbolCount
      dateCount
    }
  }
`;

/* ── removals ──────────────────────────────────────────────────────────────
   Each of these is referenced by something that has already been computed, so
   the screens ask before calling them. */

export const DELETE_ATTRIBUTION_SETTINGS = gql`
  mutation DeleteAttributionSettings($id: Int!) {
    deleteAttributionSettings(id: $id) {
      success
      message
    }
  }
`;

export const DELETE_FORECAST_ASSUMPTION = gql`
  mutation DeleteForecastAssumption($id: Int!) {
    deleteForecastAssumption(id: $id)
  }
`;

export const DELETE_BENCHMARK = gql`
  mutation DeleteBenchmark($id: Int!) {
    deleteBenchmark(id: $id)
  }
`;

export const DELETE_BENCHMARKS = gql`
  mutation DeleteBenchmarks($ids: [Int!]!) {
    deleteBenchmarks(ids: $ids)
  }
`;

export const CLONE_SECURITY_GROUP = gql`
  mutation CloneSecurityGroup(
    $source_group_id: Int!,
    $new_name: String!,
    $new_description: String,
    $is_active: Boolean
  ) {
    cloneSecurityGroup(
      sourceGroupId: $source_group_id,
      newName: $new_name,
      newDescription: $new_description,
      isActive: $is_active
    ) {
      id
      name
      description
      isActive
      isClassificationAggregation
      isValueAggregation
      isStaticTagAggregation
      isDynamicTagAggregation
      internalReferenceFieldNames
    }
  }
`;

export const DELETE_SECURITY_GROUP = gql`
  mutation DeleteSecurityGroup($id: Int!) {
    deleteSecurityGroup(id: $id)
  }
`;

export const DELETE_SECURITY_GROUPS = gql`
  mutation DeleteSecurityGroups($ids: [Int!]!) {
    deleteSecurityGroups(ids: $ids)
  }
`;

export const DELETE_USER_DEFINED_TAG = gql`
  mutation DeleteUserDefinedTag($tagId: Int!) {
    deleteUserDefinedTag(tagId: $tagId)
  }
`;

/* ── label prompts ─────────────────────────────────────────────────────────
   Each label carries a prompt the agents use when they have to talk about that
   field. These generate them — one label, or a whole group that has none. */

export const BACKFILL_UI_LABEL_AGENT_PROMPTS = gql`
  mutation BackfillUiLabelAgentPrompts($labelGroup: String, $limit: Int) {
    backfillUiLabelAgentPrompts(labelGroup: $labelGroup, limit: $limit) {
      success
      message
      filled
      skipped
      examined
    }
  }
`;

export const REGENERATE_UI_LABEL_AGENT_PROMPT = gql`
  mutation RegenerateUiLabelAgentPrompt($value: String!, $labelGroup: String) {
    regenerateUiLabelAgentPrompt(value: $value, labelGroup: $labelGroup) {
      success
      message
      label { value agentPrompt }
    }
  }
`;

/* ── translations ──────────────────────────────────────────────────────────
   Save edits to one table's translations: one input per record, each naming
   its translation id, the fields changed and whether it is verified. Results
   come back in input order; one bad record does not stop the others. */

export const UPDATE_TABLE_TRANSLATIONS = gql`
  mutation UpdateTableTranslations($table: String!, $inputs: [UpdateTableTranslationInput!]!) {
    updateTableTranslations(table: $table, inputs: $inputs) {
      success
      message
      results {
        success
        message
        updatedTranslation {
          ${TABLE_TRANSLATION_ITEM_FIELDS}
        }
      }
    }
  }
`;

/* Start a translation correction sweep. With `tables` (keys), those tables are
   corrected and only they are written; without, the cache is swept first and
   then the fields each cached text is used in. `languages` narrows either;
   omitted, every language. One run is queued per table. */
export const RUN_TRANSLATION_CORRECTION = gql`
  mutation RunTranslationCorrection($tables: [String!], $languages: [String!]) {
    runTranslationCorrection(tables: $tables, languages: $languages) {
      success
      message
      runs {
        table
        eventId
        queued
      }
    }
  }
`;

/* ── bootstrap ─────────────────────────────────────────────────────────────
   First-run loaders. On an empty database these come before everything else:
   until they have run, the planner and the model catalogue read empty. */

export const LOAD_GOAL_PLANNING_DATA = gql`
  mutation LoadGoalPlanningData(
    $includeModels: Boolean!
    $wait: Boolean!
    $force: Boolean!
  ) {
    loadGoalPlanningData(includeModels: $includeModels, wait: $wait, force: $force)
  }
`;

export const LOAD_ASSET_MODELS_FROM_FILE = gql`
  mutation LoadAssetModelsFromFile($filePath: String!, $updateIfExists: Boolean!) {
    loadAssetModelsFromFile(filePath: $filePath, updateIfExists: $updateIfExists) {
      scenarioName
      assumptionsCreated
      forecastsGenerated
      mode
      status
      error
    }
  }
`;

export const LOAD_FORECAST_FROM_FILE = gql`
  mutation LoadForecastFromFile($input: LoadForecastFromFileInput!) {
    loadForecastFromFile(input: $input) {
      scenarioId
      scenarioName
      assumptionsCreated
      forecastsGenerated
      mode
      status
      error
    }
  }
`;

export const REFRESH_SIMULATION_GRID = gql`
  mutation RefreshSimulationGrid($riskProfiles: [String!], $horizons: [Int!]) {
    refreshSimulationGrid(riskProfiles: $riskProfiles, horizons: $horizons)
  }
`;

/* ── data loads ────────────────────────────────────────────────────────────
   The loaders that bring the outside world in — scheme data, market prices,
   exchange and country stock loads. Long-running and heavy: the screen runs
   them one at a time and says so.

   The Morningstar loaders app_admin carries are NOT here. This backend has no
   `updateMstar*` mutation at all, nor `updateStockFundamentals`; app_admin
   still calls all five and gets "Cannot query field" back. */

export const SYNC_VR_MUTUAL_FUNDS_INCREMENTAL = gql`
  mutation SyncVrMutualFundsIncremental {
    syncVrMutualFundsIncremental {
      success
      message
      fundsProcessed
    }
  }
`;

export const SYNC_ALL_ASSET_MODELS = gql`
  mutation SyncAllAssetModels($includeEtfs: Boolean) {
    syncAllAssetModels(includeEtfs: $includeEtfs) {
      success
      message
      syncedCount
      failedCount
      failedModelIds
    }
  }
`;

export const UPDATE_INDIAN_MARKET_DAILY = gql`
  mutation UpdateIndianMarketDaily {
    updateIndianMarketDaily {
      success
      message
      symbolsSynced
      newIpos
      errors
    }
  }
`;

export const UPDATE_INDIAN_MARKET_COMPLETE = gql`
  mutation UpdateIndianMarketComplete {
    updateIndianMarketComplete {
      success
      message
      symbolsSynced
      newIpos
      errors
    }
  }
`;

export const UPDATE_STOCK_PRICING = gql`
  mutation UpdateStockPricing($exchange: String!) {
    updateStockPricing(exchangeCode: $exchange) {
      success
      message
      symbolsSynced
      errors
    }
  }
`;

export const UPDATE_STOCK_FINANCIALS = gql`
  mutation UpdateStockFinancials($symbol: String!) {
    updateStockFinancials(securitySymbol: $symbol) {
      success
      message
      symbolsSynced
      errors
    }
  }
`;

export const UPDATE_STOCK_COMPLETE = gql`
  mutation UpdateStockComplete($symbol: String!) {
    updateStockComplete(securitySymbol: $symbol) {
      success
      message
      symbolsSynced
      errors
    }
  }
`;

export const UPDATE_STOCKS_BATCH_COMPLETE = gql`
  mutation UpdateStocksBatchComplete($symbols: [String!]!) {
    updateStocksBatchComplete(securitySymbols: $symbols) {
      success
      message
      symbolsSynced
      errors
    }
  }
`;

export const UPDATE_EXCHANGE_STOCKS = gql`
  mutation UpdateExchangeStocks($exchange: String!) {
    updateExchangeStocks(exchangeCode: $exchange) {
      success
      message
      symbolsSynced
      newIpos
      errors
    }
  }
`;

export const UPDATE_EXCHANGE_COMPLETE = gql`
  mutation UpdateExchangeComplete($exchange: String!) {
    updateExchangeComplete(exchangeCode: $exchange) {
      success
      message
      symbolsSynced
      newIpos
      errors
    }
  }
`;

export const UPDATE_COUNTRY_STOCKS = gql`
  mutation UpdateCountryStocks($country: String!) {
    updateCountryStocks(countryCode: $country) {
      success
      message
      symbolsSynced
      errors
    }
  }
`;

export const UPDATE_SECURITY_SEARCH_INDEX = gql`
  mutation UpdateSecuritySearchIndex(
    $entityType: String
    $limit: Int
    $forceRefresh: Boolean
  ) {
    updateSecuritySearchIndex(
      entityType: $entityType
      limit: $limit
      forceRefresh: $forceRefresh
    ) {
      indexed
      failed
      message
    }
  }
`;

/* ── recalculations ────────────────────────────────────────────────────────
   What is derived from the loaded data, rebuilt. These come AFTER a load: run
   them first and they recompute the old numbers. */

export const UPDATE_MF_EXPOSURES = gql`
  mutation UpdateMfExposures(
    $startDate: String
    $endDate: String
    $lookbackDays: Int
    $isRegular: Boolean
    $isGrowth: Boolean
  ) {
    updateMfExposures(
      startDate: $startDate
      endDate: $endDate
      lookbackDays: $lookbackDays
      isRegular: $isRegular
      isGrowth: $isGrowth
    ) {
      success
      message
      factorName
      recordsCalculated
      fromDate
      toDate
    }
  }
`;

export const UPDATE_STOCK_EXPOSURES = gql`
  mutation UpdateStockExposures(
    $startDate: String
    $endDate: String
    $lookbackDays: Int
  ) {
    updateStockExposures(
      startDate: $startDate
      endDate: $endDate
      lookbackDays: $lookbackDays
    ) {
      success
      message
      factorName
      recordsCalculated
      fromDate
      toDate
    }
  }
`;

export const UPDATE_FACTOR_INDEX_EXPOSURES = gql`
  mutation UpdateFactorIndexExposures($startDate: String, $endDate: String) {
    updateFactorIndexExposures(startDate: $startDate, endDate: $endDate) {
      success
      message
      recordsUpserted
      startDate
      endDate
    }
  }
`;

export const UPDATE_VOL_ADJUSTED_EXPOSURES = gql`
  mutation UpdateVolAdjustedExposures($startDate: String, $endDate: String) {
    updateVolAdjustedExposures(startDate: $startDate, endDate: $endDate) {
      success
      message
      recordsUpserted
      startDate
      endDate
    }
  }
`;

export const UPDATE_FACTOR_PORTFOLIOS = gql`
  mutation UpdateFactorPortfolios(
    $factorNames: [String!]
    $startDate: Date
    $endDate: Date
    $frequency: String
    $updateSectors: Boolean
    $updateMarketCaps: Boolean
    $weightingStrategy: String
  ) {
    updateFactorPortfolios(
      factorNames: $factorNames
      startDate: $startDate
      endDate: $endDate
      frequency: $frequency
      updateSectors: $updateSectors
      updateMarketCaps: $updateMarketCaps
      weightingStrategy: $weightingStrategy
    ) {
      success
      message
    }
  }
`;

export const CALCULATE_FACTOR_PORTFOLIO_RETURNS = gql`
  mutation CalculateFactorPortfolioReturns(
    $securitySymbol: String!
    $startDate: String
    $endDate: String
  ) {
    calculateFactorPortfolioReturns(
      securitySymbol: $securitySymbol
      startDate: $startDate
      endDate: $endDate
    ) {
      success
      message
    }
  }
`;

export const COMPUTE_ASSET_MODEL_EXPOSURES = gql`
  mutation ComputeAssetModelExposures($startDate: String, $endDate: String) {
    computeAssetModelExposures(startDate: $startDate, endDate: $endDate) {
      success
      message
      recordsUpserted
      startDate
      endDate
    }
  }
`;

export const COMPUTE_BEHAVIORAL_HIGHLIGHTS = gql`
  mutation ComputeBehavioralHighlights(
    $securitySymbol: String!
    $benchmarkSymbol: String
    $userHorizon: String
    $userRisk: String
    $useAgent: Boolean
    $goalId: Int
  ) {
    computeBehavioralHighlights(
      securitySymbol: $securitySymbol
      benchmarkSymbol: $benchmarkSymbol
      userHorizon: $userHorizon
      userRisk: $userRisk
      useAgent: $useAgent
      goalId: $goalId
    )
  }
`;

export const RECOMPUTE_GOAL_DATA = gql`
  mutation RecomputeGoalData($scenarioId: Int!, $includeModels: Boolean!, $goalTypes: [String!]) {
    recomputeGoalData(scenarioId: $scenarioId, includeModels: $includeModels, goalTypes: $goalTypes) {
      scenarioId
      forecastsGenerated
      simulationsGenerated
      gridRows
      presetRows
      modelRows
      status
      error
    }
  }
`;
